// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class HceEncryptionService {
  private readonly DEFAULT_KEY = environment.apiBaseUrl + environment.appName;

  /**
   * Encrypt a UTF-8 string and produce a Base64URL (no padding) token.
   * @param plainText - The input string to encrypt.
   * @param cryptKey  - A passphrase-like string used to derive the AES key and IV.
   * @returns A Base64URL (no padding) string, or null on error.
   */
  async encrypt(plainText: string, cryptKey: string = this.DEFAULT_KEY): Promise<string | null> {
    try {
      // fallback
      if (!window.crypto?.subtle) {
        console.warn('[HceEncryptionService.encrypt] fallback mode');
        const bytes = this.utf8ToBytes(plainText);
        const b64 = this.bytesToBase64(bytes);
        return this.base64UrlEncodeNoPad(b64);
      }

      const ivLen = 16; // AES-CBC uses a 16-byte IV
      const salt = this.randomBytes(ivLen);

      // Derive AES-256 key: key = SHA256( cryptKey || salt )
      const keyBytes = await this.sha256(this.concatBytes(this.utf8ToBytes(cryptKey), salt));

      // Derive IV: iv = first 16 bytes of SHA256( key || cryptKey || salt )
      const ivHash = await this.sha256(this.concatBytes(keyBytes, this.utf8ToBytes(cryptKey), salt));
      const iv = ivHash.slice(0, ivLen);

      // Encrypt plaintext (UTF-8) with AES-CBC and PKCS#7 padding (handled by Web Crypto)
      const cipherRaw = await this.aesCbcEncryptRaw(this.utf8ToBytes(plainText), keyBytes, iv);

      // Output bytes: salt || ciphertext
      const outBytes = this.concatBytes(salt, cipherRaw);

      // Encode once as Base64URL (no padding) for URL-safe usage
      const b64 = this.bytesToBase64(outBytes);
      return this.base64UrlEncodeNoPad(b64);
    } catch (e) {
      console.error('[HceEncryptionService.encrypt] error:', e);
      return null;
    }
  }

  /**
   * Decrypt a Base64URL (no padding) token back to a UTF-8 string.
   * @param token    - Base64URL (no padding) string produced by `encrypt`.
   * @param cryptKey - The same key string used during encryption.
   * @returns The decrypted UTF-8 string, or null on error.
   */
  async decrypt(token: string, cryptKey: string = this.DEFAULT_KEY): Promise<string | null> {
    try {
      // Decode Base64URL into bytes
      const outerBytes = this.base64UrlToBytes(token);

      // fallback
      if (!window.crypto?.subtle || outerBytes.length < 32) {
        try {
          return this.bytesToUtf8(outerBytes);
        } catch {
          return null;
        }
      }

      // Expect at least 16 bytes of salt and at least one block of ciphertext (>= 16 bytes)
      if (outerBytes.length < 32) return null;

      const salt = outerBytes.slice(0, 16);
      const cipherRaw = outerBytes.slice(16);

      // Re-derive AES key and IV
      const keyBytes = await this.sha256(this.concatBytes(this.utf8ToBytes(cryptKey), salt));
      const ivHash = await this.sha256(this.concatBytes(keyBytes, this.utf8ToBytes(cryptKey), salt));
      const iv = ivHash.slice(0, 16);

      // Decrypt ciphertext
      const plainBytes = await this.aesCbcDecryptRaw(cipherRaw, keyBytes, iv);
      return this.bytesToUtf8(plainBytes);
    } catch (e) {
      console.error('[HceEncryptionService.decrypt] error:', e);
      return null;
    }
  }

  // ===== Web Crypto helpers =====

  private async aesCbcEncryptRaw(data: Uint8Array, keyBytes: Uint8Array, iv: Uint8Array): Promise<Uint8Array> {
    const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-CBC' }, false, ['encrypt']);
    const buf = await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, key, data);
    return new Uint8Array(buf);
  }

  private async aesCbcDecryptRaw(data: Uint8Array, keyBytes: Uint8Array, iv: Uint8Array): Promise<Uint8Array> {
    const key = await crypto.subtle.importKey('raw', keyBytes, { name: 'AES-CBC' }, false, ['decrypt']);
    const buf = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, key, data);
    return new Uint8Array(buf);
  }

  private async sha256(data: Uint8Array): Promise<Uint8Array> {
    const buf = await crypto.subtle.digest('SHA-256', data);
    return new Uint8Array(buf);
  }

  // ===== Encoding utilities =====

  private utf8ToBytes(s: string): Uint8Array {
    return new TextEncoder().encode(s);
  }
  private bytesToUtf8(b: Uint8Array): string {
    return new TextDecoder().decode(b);
  }

  private bytesToBase64(bytes: Uint8Array): string {
    // Convert binary data to Base64 via btoa on a binary string
    const chunk = 0x8000;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  }

  private base64ToBytes(b64: string): Uint8Array {
    const binary = atob(b64);
    const out = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
    return out;
  }

  private base64UrlEncodeNoPad(stdB64: string): string {
    // Make Base64 URL-safe and strip padding
    return stdB64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  private base64UrlToBytes(b64url: string): Uint8Array {
    // Restore to standard Base64 and add missing padding if necessary
    const std = b64url.replace(/-/g, '+').replace(/_/g, '/');
    const padLen = (4 - (std.length % 4)) % 4;
    return this.base64ToBytes(std + '='.repeat(padLen));
  }

  // ===== Byte utilities =====

  private concatBytes(...arrs: Uint8Array[]): Uint8Array {
    const len = arrs.reduce((acc, a) => acc + a.length, 0);
    const out = new Uint8Array(len);
    let off = 0;
    for (const a of arrs) {
      out.set(a, off);
      off += a.length;
    }
    return out;
  }

  /**
   * Generate cryptographically secure random bytes.
   * Kept as a separate method to ease spying/stubbing in unit tests.
   */
  protected randomBytes(len: number): Uint8Array {
    const b = new Uint8Array(len);
    crypto.getRandomValues(b);
    return b;
  }
}
